/**
 * Per-link trace context for the SPA.
 *
 * The /r/<token> redirect handler sets a one-shot `ori_trace` cookie
 * (Max-Age=60, NOT HttpOnly) carrying the trace_id minted by
 * EffectWorker. On first paint the SPA reads the cookie, stores the
 * id in this module-level singleton, and immediately deletes the
 * cookie so a refresh / share-link / back-nav doesn't restart the
 * trace.
 *
 * `getTraceId()` is then used by `apiFetch` to attach
 * `X-Ori-Trace-Id` to every backend call, and by `logEvent()` to
 * tag client telemetry beacons. When the SPA was reached without
 * going through /r/ (direct navigation, refresh after redirect),
 * `getTraceId()` returns '' and the backend will mint an orphan
 * trace_id of its own.
 */

const TRACE_COOKIE_NAME = 'ori_trace';
const TRACE_ID_RE = /^tr_[A-HJ-NP-TV-Z2-9]{12}$/;

let currentTraceId = '';

function readCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const all = document.cookie || '';
  if (!all) return '';
  for (const part of all.split(';')) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq < 0) continue;
    if (trimmed.slice(0, eq) === name) {
      try {
        return decodeURIComponent(trimmed.slice(eq + 1));
      } catch {
        return trimmed.slice(eq + 1);
      }
    }
  }
  return '';
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  // Mirror the attributes used at write time so the browser matches
  // and discards the entry.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Path=/; SameSite=Lax; Max-Age=0${secure}`;
}

/**
 * Bootstrap the trace context on first paint. Must be called before
 * any `getTraceId()` consumer fires. main.tsx calls this before
 * mounting the React tree.
 */
export function bootstrapTraceFromCookie(): void {
  const fromCookie = readCookie(TRACE_COOKIE_NAME);
  if (fromCookie && TRACE_ID_RE.test(fromCookie)) {
    currentTraceId = fromCookie;
  }
  // One-shot: delete the cookie regardless so a refresh doesn't
  // re-pick the same trace and conflate distinct sessions.
  deleteCookie(TRACE_COOKIE_NAME);
}

export function getTraceId(): string {
  return currentTraceId;
}

/**
 * Replace the active trace_id. Used in tests; production code should
 * never need this — the trace_id flows in via cookie at boot.
 */
export function setTraceId(value: string): void {
  if (!value || !TRACE_ID_RE.test(value)) {
    currentTraceId = '';
    return;
  }
  currentTraceId = value;
}

export function clearTraceId(): void {
  currentTraceId = '';
}
